import React, { CSSProperties, useEffect, useRef } from 'react';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import gsap from 'gsap';

/**
 * Diagonal "Registered" corner ribbon — high-visibility green stamp.
 *
 * A vivid green ribbon rotated -45° anchored to the top-left corner of a
 * tournament card. Green was chosen specifically for contrast: the card
 * body uses a warm parchment/gold gradient (#e3cf9f → #c2ad7a) and
 * tournament banner art is warm-toned, so the gold original ribbon blended
 * in. Green pops against both warm cards and dark banner images, and reads
 * as "confirmed" semantically.
 *
 * Parent card MUST have `position: relative` AND `overflow: hidden` so the
 * rotated ribbon clips cleanly to the corner. `pointer-events: none` on
 * both the container and the inner layer so the banner never steals the
 * card's click target.
 */
const RegisteredBanner = () => {
  const { reduceAnimations } = useAnimationPreferences();
  const ribbonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ribbonRef.current) return;
    if (reduceAnimations) {
      gsap.set(ribbonRef.current, { scale: 1, opacity: 1, rotate: -45 });
      return;
    }
    // Stamp-in entrance: a slight overshoot scale so the ribbon "lands" on
    // the card when it first appears. Matches the rest of the lobby's
    // `back.out` entry feel (RegisteredPill, NetflixLobby cards). Tweens
    // captured + killed on unmount because tournament-list refresh
    // remounts cards.
    const tween = gsap.fromTo(
      ribbonRef.current,
      { scale: 0.6, opacity: 0, rotate: -90 },
      { scale: 1, opacity: 1, rotate: -45, duration: 0.5, ease: 'back.out(1.6)' }
    );
    return () => {
      tween.kill();
    };
  }, [reduceAnimations]);

  return (
    <div style={containerStyle} data-testid='registered-banner'>
      <div ref={ribbonRef} style={ribbonStyle}>
        Registered
      </div>
    </div>
  );
};

export default RegisteredBanner;

const containerStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 110,
  height: 110,
  overflow: 'hidden',
  pointerEvents: 'none',
  zIndex: 5
};

// Vivid green gradient — stands out against the card's warm parchment/gold
// tones and dark tournament banner images alike. White text + dark text-shadow
// keeps legibility across both light and dark backgrounds.
const ribbonStyle: CSSProperties = {
  position: 'absolute',
  top: 22,
  left: -38,
  width: 160,
  padding: '4px 0',
  transform: 'rotate(-45deg)',
  transformOrigin: 'center center',
  backgroundImage: 'linear-gradient(135deg, #166534 0%, #0b9c40 50%, #166534 100%)',
  color: '#ffffff',
  fontSize: '0.7rem',
  fontWeight: 800,
  letterSpacing: '0.1em',
  textAlign: 'center',
  fontFamily: "'Rye', 'Outfit', serif",
  boxShadow:
    '0 2px 5px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.25), inset 0 -1px 0 rgba(0, 0, 0, 0.3)',
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
  borderTop: '1px solid rgba(255, 255, 255, 0.25)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.4)',
  pointerEvents: 'none'
};
